import { useEffect, useState } from 'react';
import {
  PROVIDERS,
  loadConnection,
  saveConnection,
  clearConnection,
  providerMeta,
  oauthApp,
  backendFor,
  runStorageTest,
  type Connection,
  type ProviderId,
  type ProviderMeta,
  type TestResult,
} from '../lib/providers';
import { beginAuth, completeAuth } from '../lib/oauth';
import { corsPolicy, dashboardLinks, type R2Config } from '../lib/r2';
import { parseR2Paste, FIELD_LABELS } from '../lib/parseR2';

const EMPTY_R2: R2Config = { accountId: '', accessKeyId: '', secretAccessKey: '', bucket: '' };

/**
 * Picking and connecting a place to keep envelopes.
 *
 * Used both inside the wizard's first step and on its own tab, so the two can
 * never drift apart.
 */
export function StorageSetup({ onConnected }: { onConnected?: () => void }) {
  const [conn, setConn] = useState<Connection | null>(loadConnection());
  const [choosing, setChoosing] = useState<ProviderId | null>(null);
  const [result, setResult] = useState<TestResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  // Finish an OAuth round-trip if we have just come back from one.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const done = await completeAuth(oauthApp);
        if (!done || cancelled) return;
        setBusy(true);
        const candidate: Connection = { provider: done.providerId as ProviderId, tokens: done.tokens };
        const test = await runStorageTest(await backendFor(candidate));
        if (cancelled) return;
        setResult(test);
        if (test.ok) {
          const saved = { ...candidate, verifiedAt: Date.now() };
          saveConnection(saved);
          setConn(saved);
          onConnected?.();
        }
      } catch (e) {
        if (!cancelled) setError(e instanceof Error ? e.message : String(e));
      } finally {
        if (!cancelled) setBusy(false);
      }
    })();
    return () => {
      cancelled = true;
    };
    // Runs once on mount: the redirect is only ever handled on first render.
     
  }, []);

  async function connectOAuth(meta: ProviderMeta) {
    setError(null);
    try {
      await beginAuth(meta.id, meta.app!);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    }
  }

  function disconnect() {
    clearConnection();
    setConn(null);
    setResult(null);
    setChoosing(null);
    onConnected?.();
  }

  if (conn?.verifiedAt) {
    const meta = providerMeta(conn.provider);
    return (
      <div className="connected">
        <div className="row-between">
          <div>
            <strong>{meta.name}</strong>
            <p className="muted">
              Connected. Envelopes are written straight there from this tab.
            </p>
          </div>
          <span className="tag on">Connected</span>
        </div>
        {meta.durability.lasts !== 'indefinite' && (
          <p className="hint">{meta.durability.note}</p>
        )}
        <button type="button" onClick={disconnect}>
          Disconnect
        </button>
      </div>
    );
  }

  if (choosing === 'r2') {
    return <R2Setup onBack={() => setChoosing(null)} onConnected={(c) => { setConn(c); onConnected?.(); }} />;
  }

  return (
    <>
      {busy && <p className="hint">Checking the connection…</p>}
      {error && <p className="error">{error}</p>}
      {result && !result.ok && <TestReport result={result} />}

      <ul className="picker">
        {PROVIDERS.map((p) => (
          <li key={p.id}>
            <div className="pick-head">
              <div>
                <strong>{p.name}</strong>
                <span className="muted"> · {p.free}</span>
                <p className="muted">{p.blurb}</p>
              </div>
              {p.auth === 'oauth' ? (
                <button
                  className="primary"
                  disabled={p.needsSetup || busy}
                  onClick={() => void connectOAuth(p)}
                >
                  {p.needsSetup ? 'Needs setup' : 'Connect'}
                </button>
              ) : (
                <button disabled={busy} onClick={() => setChoosing(p.id)}>
                  Set up
                </button>
              )}
            </div>
            <p className={`durability ${p.durability.lasts}`}>{p.durability.note}</p>
          </li>
        ))}
      </ul>

      {PROVIDERS.some((p) => p.needsSetup) && (
        <p className="hint setup-note">
          Sign-in providers marked <em>Needs setup</em> have no client id built into
          this deployment yet. Registering them is a one-off — see{' '}
          <code>docs/ONE_CLICK_STORAGE.md</code>. Cloudflare R2 works now.
        </p>
      )}
    </>
  );
}

/** R2 keeps its own flow: keys and a CORS policy, not a sign-in. */
function R2Setup({ onBack, onConnected }: { onBack: () => void; onConnected: (c: Connection) => void }) {
  const [config, setConfig] = useState<R2Config>(EMPTY_R2);
  const [paste, setPaste] = useState('');
  const [detected, setDetected] = useState<string[] | null>(null);
  const [manual, setManual] = useState(false);
  const [result, setResult] = useState<TestResult | null>(null);
  const [busy, setBusy] = useState(false);

  const links = dashboardLinks(config);
  const complete =
    config.accountId.trim() && config.accessKeyId.trim() && config.secretAccessKey.trim() && config.bucket.trim();

  function absorb(text: string) {
    setPaste(text);
    if (!text.trim()) return setDetected(null);
    const p = parseR2Paste(text);
    setConfig((c) => ({
      accountId: p.accountId ?? c.accountId,
      bucket: p.bucket ?? c.bucket,
      accessKeyId: p.accessKeyId ?? c.accessKeyId,
      secretAccessKey: p.secretAccessKey ?? c.secretAccessKey,
    }));
    setDetected(p.found.map((f) => FIELD_LABELS[f]));
    setResult(null);
    if (p.found.length < 4) setManual(true);
  }

  async function test() {
    setBusy(true);
    setResult(null);
    try {
      const candidate: Connection = { provider: 'r2', keys: { ...config } };
      const res = await runStorageTest(await backendFor(candidate));
      setResult(res);
      if (res.ok) {
        const saved = { ...candidate, verifiedAt: Date.now() };
        saveConnection(saved);
        onConnected(saved);
      }
    } finally {
      setBusy(false);
    }
  }

  return (
    <>
      <button type="button" onClick={onBack}>
        ← Other providers
      </button>

      <div className="oneclick">
        <a className="btn gold" href={links.overview} target="_blank" rel="noopener noreferrer">
          Set up R2 on Cloudflare →
        </a>
        <p className="hint">
          Switch R2 on if you have not already, create a bucket, then make an{' '}
          <strong>API token</strong> with <strong>Object Read &amp; Write</strong>.
          Cloudflare does not allow this page to do it for you.
        </p>
      </div>

      <label>
        Paste what Cloudflare showed you
        <textarea
          rows={4}
          value={paste}
          onChange={(e) => absorb(e.target.value)}
          placeholder={'Select the whole credentials panel and paste it here.'}
          spellCheck={false}
        />
      </label>

      {detected && (
        <p className={detected.length === 4 ? 'ok' : 'hint'}>
          {detected.length ? `Read: ${detected.join(', ')}.` : 'Nothing recognised in that.'}
          {detected.length < 4 && ' Fill in the rest below.'}
        </p>
      )}

      {(manual || !detected) && (
        <div className="fields">
          {(['accountId', 'bucket', 'accessKeyId', 'secretAccessKey'] as const).map((k) => (
            <label key={k}>
              {FIELD_LABELS[k]}
              <input
                type={k === 'secretAccessKey' ? 'password' : 'text'}
                value={config[k]}
                onChange={(e) => setConfig((c) => ({ ...c, [k]: e.target.value.trim() }))}
                spellCheck={false}
              />
            </label>
          ))}
        </div>
      )}

      <details className="cors">
        <summary>One more thing: let this page write to the bucket</summary>
        <p className="hint">
          R2 blocks browsers unless the bucket says otherwise. Paste this into the
          bucket's <strong>Settings → CORS policy</strong>:
        </p>
        <pre>{corsPolicy(window.location.origin)}</pre>
        <div className="nav start">
          <button className="btn" type="button" onClick={() => void navigator.clipboard.writeText(corsPolicy(window.location.origin))}>
            Copy the policy
          </button>
          <a className="btn" href={links.bucketSettings} target="_blank" rel="noopener noreferrer">
            Open bucket settings →
          </a>
        </div>
      </details>

      <button className="primary wide" disabled={!complete || busy} onClick={() => void test()}>
        {busy ? 'Testing…' : 'Connect and test'}
      </button>

      {result && <TestReport result={result} corsHint={links.bucketSettings} />}
    </>
  );
}

export function Storage({ onChange }: { onChange?: () => void }) {
  return (
    <section className="card">
      <p className="eyebrow">Where envelopes live</p>
      <h2>Your storage</h2>
      <p className="lede">
        Pick somewhere you own. Envelopes are written straight there from this
        tab — the keys never leave this browser, and there is no server in the
        path.
      </p>
      <StorageSetup onConnected={onChange} />
    </section>
  );
}

function TestReport({ result, corsHint }: { result: TestResult; corsHint?: string }) {
  const blocked = result.steps.some((s) => !s.ok && /allowing requests/i.test(s.detail ?? ''));
  return (
    <div className={`report ${result.ok ? 'good' : 'bad'}`}>
      <ul>
        {result.steps.map((s, i) => (
          <li key={i}>
            <span className={s.ok ? 'tick' : 'cross'}>{s.ok ? '✓' : '✕'}</span>
            <span>
              {s.label}
              {s.detail && <span className="muted"> — {s.detail}</span>}
            </span>
          </li>
        ))}
      </ul>
      {blocked && corsHint && (
        <p className="hint">
          The bucket has not been told to accept requests from this page. Paste the
          CORS policy above into{' '}
          <a href={corsHint} target="_blank" rel="noopener noreferrer">
            the bucket's settings
          </a>
          , then test again.
        </p>
      )}
    </div>
  );
}
